import {IMAGE_MIME_TYPE} from '@mantine/dropzone';
import type {InstanceResponse} from 'src/api/ProactApiClient';
import {RoleEnum, UserTypeEnum} from 'src/api/ProactApiClient';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

export const createFormMeta = (userGroups: ISelectOption[]): IFormFieldV2[] => [
  {
    name: 'name',
    label: 'Instance Name',
    type: 'text',
    required: true,
    textboxOptions: {
      validationType: 'name',
    },
  },
  {
    name: 'heading1',
    label: 'Feature Flag Settings',
    type: 'heading',
  },
  {
    name: 'actions',
    label: 'Enable Actions Module',
    type: 'switch',
  },
  {
    name: 'analytics',
    label: 'Enable Analytics Module',
    type: 'switch',
  },
  {
    name: 'approvals',
    label: 'Enable Approvals Module',
    type: 'switch',
  },
  {
    name: 'auditLogs',
    label: 'Enable Audit Log Module',
    type: 'switch',
    description:
      'Default audit log retention period is 7 days. Contact IT to change rentention period for this instance.',
  },
  {
    name: 'bulletins',
    label: 'Enable Bulletins Module',
    type: 'switch',
  },
  {
    name: 'compliance',
    label: 'Enable Compliance Module',
    type: 'switch',
  },
  {
    name: 'documentManagement',
    label: 'Enable Document Management Module',
    type: 'switch',
  },
  {
    name: 'forms',
    label: 'Enable Forms Module',
    type: 'switch',
  },
  {
    name: 'itemTracker',
    label: 'Enable Item Tracker Module',
    type: 'switch',
  },
  {
    name: 'jobHazardAnalysis',
    label: 'Enable JHA Module',
    type: 'switch',
  },
  {
    name: 'safetyEvents',
    label: 'Enable Safety Events Module',
    type: 'switch',
  },
  {
    name: 'toolboxTalks',
    label: 'Enable Toolbox Talks Module',
    type: 'switch',
  },
  {
    name: 'training',
    label: 'Enable Training Module',
    type: 'switch',
  },
  {
    name: 'vendors',
    label: 'Enable Vendors Module',
    type: 'switch',
  },
  {
    name: 'workflows',
    label: 'Enable Workflows Module',
    type: 'switch',
  },
  {
    name: 'heading2',
    label: 'Notification Settings',
    type: 'heading',
  },
  {
    name: 'emailPlaintextOnly',
    label: 'Is Email Plaintext Only',
    type: 'switch',
  },
  {
    name: 'heading3',
    label: 'Authentication Settings',
    type: 'heading',
  },
  {
    name: 'mfaRequired',
    label: 'Multi-factor Authentication Required',
    type: 'switch',
  },
  {
    name: 'ssoSelfRegistrationEnabled',
    label: 'Enable SSO Self-Registration',
    type: 'switch',
    description:
      'When enabled, users who authenticate via SSO but do not have an account will be automatically created. Only applies when SSO is configured for the instance.',
  },
  {
    name: 'ssoSelfRegistrationDefaultRole',
    label: 'SSO Self-Registration - Default Role',
    type: 'select',
    options: [
      {id: RoleEnum.InstanceUser, label: 'Instance User'},
      {id: RoleEnum.InstanceObserver, label: 'Instance Observer'},
      {id: RoleEnum.InstanceSupervisor, label: 'Instance Supervisor'},
      {id: RoleEnum.SafetyRepresentative, label: 'Safety Representative'},
      {id: RoleEnum.NotificationOnly, label: 'Notification Only'},
    ],
    description:
      'Role assigned to users created via SSO self-registration. Defaults to Instance User if not set.',
  },
  {
    name: 'ssoSelfRegistrationDefaultUserType',
    label: 'SSO Self-Registration - Default User Type',
    type: 'select',
    options: [
      {id: UserTypeEnum.Employee, label: 'Employee'},
      {id: UserTypeEnum.Contractor, label: 'Contractor'},
    ],
    description:
      'User type assigned to users created via SSO self-registration. Affects default group assignment. Defaults to Employee if not set.',
  },
  {
    name: 'ssoSelfRegistrationDefaultUserGroupId',
    label: 'SSO Self-Registration - Default User Group',
    type: 'select',
    options: userGroups,
    description:
      'Specific user group to assign to users created via SSO self-registration.',
  },
  {
    name: 'heading4',
    label: 'Badge Expiration Settings',
    type: 'heading',
  },
  {
    name: 'isBadgeExpirationEnabled',
    label: 'Enable Badge Expiration',
    type: 'switch',
    description:
      'When enabled, contractors with an expired badge expiration date will be automatically deactivated by a daily scheduled job.',
  },
  {
    name: 'defaultBadgeExpirationDays',
    label: 'Default Badge Expiration Days',
    type: 'number',
    description:
      'Number of days from signup until badge expires for new contractors. Leave empty to require manual entry.',
  },
  {
    name: 'badgeExpirationDeactivationNote',
    label: 'Badge Expiration Deactivation Note',
    type: 'text',
    description:
      "Custom note text added to a contractor's profile when they are deactivated due to badge expiration. If empty, a default message will be used.",
  },
  {
    name: 'heading5',
    label: 'Other Settings',
    type: 'heading',
  },
  {
    name: 'isAvatarRequiredEmployees',
    label: 'Show User Avatar Upload to Employees',
    type: 'switch',
    description:
      "If enabled, users with user type 'Employee' will be prompted to upload an avatar for their user profile when they login. After the avatar is uploaded, they are no longer prompted.",
  },
  {
    name: 'isAvatarRequiredContractors',
    label: 'Show User Avatar Upload to Contractors',
    type: 'switch',
    description:
      "If enabled, users with user type 'Contractor' will be prompted to upload an avatar for their user profile when they login. After the avatar is uploaded, they are no longer prompted.",
  },
  {
    name: 'logoUrl',
    label: 'Logo',
    type: 'file',
    fileUploadOptions: {
      allowMultiple: false,
      allowedFileTypes: IMAGE_MIME_TYPE,
      isHideUploadSuccessEnabled: true,
      renderUploadsAsImage: true,
      maxFileSize: 512000,
    },
  },
];

export const CreateDefaultInstance = (): InstanceResponse => {
  return {
    name: '',
    featureFlagsMetadata: {
      actions: false,
      analytics: false,
      approvals: false,
      auditLogs: false,
      bulletins: false,
      compliance: false,
      documentManagement: false,
      forms: false,
      itemTracker: false,
      jobHazardAnalysis: false,
      safetyEvents: false,
      toolboxTalks: false,
      training: false,
      vendors: false,
      workflows: false,
    },
    emailPlaintextOnly: false,
    mfaRequired: false,
    isAvatarRequiredEmployees: false,
    isAvatarRequiredContractors: false,
    ssoSelfRegistrationEnabled: false,
    ssoSelfRegistrationDefaultRole: RoleEnum.InstanceUser,
    ssoSelfRegistrationDefaultUserType: UserTypeEnum.Employee,
    isBadgeExpirationEnabled: false,
  } as InstanceResponse;
};

export const instanceDocumentTitle: string = 'Instance';
